/**
 * Thin API client for the job-broadcast endpoints.
 *
 * Auth rides on the signed tokens the global apiAuthInterceptor attaches to
 * every fetch (employer / admin / recruiter), same as the rest of the
 * dashboard — nothing is passed explicitly here.
 */
import { API_BASE_URL } from '@/config/api';

async function request(path, { method = 'GET', body } = {}) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (e) {
    data = null;
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || 'Something went wrong. Please try again.');
    err.status = res.status;
    err.code = data && data.code;
    err.fields = data && data.fields;
    throw err;
  }
  return data;
}

const base = (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/broadcast`;

export const getBroadcast = (jobId) => request(base(jobId));

export const publishJob = (jobId, { publish, includeCompany = false }) =>
  request(`${base(jobId)}/publish`, {
    method: 'POST',
    body: { publish, include_company: includeCompany },
  });

export const saveListingDetails = (jobId, details) =>
  request(`${base(jobId)}/details`, { method: 'PUT', body: details });

export const setChannelEnabled = (jobId, channel, enabled) =>
  request(`${base(jobId)}/channels/${encodeURIComponent(channel)}`, {
    method: 'PATCH',
    body: { enabled },
  });

export const recordShare = (jobId, channel) =>
  request(`${base(jobId)}/share`, { method: 'POST', body: { channel } });

// ---- Connected accounts ---------------------------------------------------

/** Where LinkedIn sends the user back after they approve (must be registered
 *  in the LinkedIn app and allowed by the API). */
export const linkedInRedirectUri = () =>
  `${window.location.origin}/hirewithus/linkedin/callback`;

export const linkedInAuthorize = () =>
  request('/api/broadcast/linkedin/authorize', {
    method: 'POST',
    body: { redirect_uri: linkedInRedirectUri() },
  });

export const linkedInCallback = ({ code, state }) =>
  request('/api/broadcast/linkedin/callback', {
    method: 'POST',
    body: { code, state, redirect_uri: linkedInRedirectUri() },
  });

export const linkedInDisconnect = () =>
  request('/api/broadcast/linkedin', { method: 'DELETE' });

export const postToLinkedIn = (jobId, text) =>
  request(`${base(jobId)}/linkedin/post`, { method: 'POST', body: { text } });

export const telegramConnect = (jobId, channel) =>
  request(`${base(jobId)}/telegram/connect`, { method: 'POST', body: { channel } });

export const telegramSetAutoPost = (jobId, autoPost) =>
  request(`${base(jobId)}/telegram`, { method: 'PATCH', body: { auto_post: autoPost } });

export const telegramDisconnect = (jobId) =>
  request(`${base(jobId)}/telegram`, { method: 'DELETE' });

export const postToTelegram = (jobId) =>
  request(`${base(jobId)}/telegram/post`, { method: 'POST' });
